import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { resourcesServices } from "../../services/resource.service";
import { IGetRefuelsRequestParams } from "../../domain/IGetRefuelsRequestParams";
import { IRefuel } from "../../domain/IRefuel";
import { IInput } from "Dashboard/scenes/Consumables/domain/IInput";
import { consumablesService } from "Dashboard/scenes/Consumables/services/consumables.service";
import { IConsumableItem } from "../../domain/IConsumableItem";
import { IGetConsumptionResponse } from "../../domain/IConsumptionResponse";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IRefuelResourceRequestParams } from "../../domain/IRefuelResourceRequestParams";
import { IUpdateRefuel } from "../../domain/IUpdateRefuel";
import { IDeleteRefuel } from "../../domain/IDeleteRefuel";

export const getFuelsAndLubricants = createAsyncThunk(
  "Resources/getFuelsAndLubricants",
  async (params: IGetRefuelsRequestParams) => {
    try {
      return await resourcesServices.getRefuels(params);
    } catch (err) {
      setError(err);
    }
  }
);

export const getConsumables = createAsyncThunk(
  "Resources/getConsumables",
  async () => {
    try {
      const consumablesResponse = await consumablesService.getInputs(
        { categories: ["FUEL"] },
        { fetchMode: 'replace' },
        new AbortController().signal
      );
      return consumablesResponse.inputs;
    } catch (err) {
      setError(err);
    }
  }
);

export const getConsumption = createAsyncThunk(
  "Resources/getConsumption",
  async (resourceId: number) => {
    try {
      return await resourcesServices.getConsumption(resourceId);
    } catch (err) {
      setError(err);
    }
  }
);

export const addRefuel = createAsyncThunk(
  "Resources/addRefuel",
  async (payload: { resourceId: number; data: IRefuelResourceRequestParams }, { rejectWithValue }) => {
    try {
      const { resourceId, data } = payload;
      const response = await resourcesServices.refuelResource(resourceId, data);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const updateRefuel = createAsyncThunk(
  "ConcreteBase/updateRefuel",
  async (data: IUpdateRefuel, { rejectWithValue }) => {
    try {
      return await resourcesServices.updateRefuel(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const deleteRefuel = createAsyncThunk(
  "ConcreteBase/deleteRefuel",
  async (data: IDeleteRefuel, { rejectWithValue }) => {
    try {
      const updatedResourceDetails = await resourcesServices.deleteRefuel(data);
      return { request: data, resourceDetails: updatedResourceDetails };
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

interface fuelsAndLubricantsState {
  loading: boolean;
  refuels: IRefuel[];
  refuelToDelete: IRefuel | undefined;
  totalKilometers: number;
  totalLiters: number;
  consumablesLoading: boolean;
  consumables: IInput[];
  consumptionLoading: boolean;
  consumption?: IGetConsumptionResponse;
  fuelLubricantsPopover: IPopoverState;
  averageConsumptionPopover: IPopoverState;
  error: any;
  fuelMap: { [key in number | string]: number | undefined };
}

const initialState: fuelsAndLubricantsState = {
  loading: false,
  refuels: [],
  refuelToDelete: undefined,
  totalKilometers: 0,
  totalLiters: 0,
  consumablesLoading: false,
  consumables: [],
  consumptionLoading: false,
  consumption: undefined,
  fuelLubricantsPopover: { isOpen: false },
  averageConsumptionPopover: { isOpen: false },
  error: null,
  fuelMap: {},
};

export const fuelsAndLubricantsSlice = createSlice({
  name: "resources-fuelsAndLubricants",
  initialState: initialState,
  reducers: {
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setFuelLuricantsPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.fuelLubricantsPopover = action.payload;
    },
    setAverageConsumptionPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.averageConsumptionPopover = action.payload;
    },
    setFuelMap: (state, action: PayloadAction<IConsumableItem>) => {
      const option = action.payload;
      if (option.quantityAmount) {
        state.fuelMap[option.consumableInputId] = option.quantityAmount;
      } else {
        delete state.fuelMap[option.consumableInputId];
      }
    },
    clearFuelMap: (state) => {
      state.fuelMap = {};
    },
    updateRefuelTemporarily: (state, action: PayloadAction<IUpdateRefuel>) => {
      for (let refuelIndex = 0; refuelIndex < state.refuels.length; refuelIndex++) {
        if (state.refuels[refuelIndex].refuelId === action.payload.refuelId) {
          if (action.payload.usageAmount !== undefined) {
            state.refuels[refuelIndex].usageAmount = action.payload.usageAmount;
          }
          if (action.payload.note !== undefined) {
            state.refuels[refuelIndex].note = action.payload.note;
          }
        }
      }
    },
    requestDeletingRefuel: (state, action: PayloadAction<IRefuel | undefined>) => {
      state.refuelToDelete = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getFuelsAndLubricants.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getFuelsAndLubricants.fulfilled, (state, action) => {
      state.loading = false;
      state.refuels = action.payload?.refuels ? action.payload.refuels : [];
      state.totalKilometers = action.payload?.totalKilometers ? action.payload.totalKilometers : 0;
      state.totalLiters = action.payload?.totalLiters ? action.payload.totalLiters : 0;
    });
    builder.addCase(getFuelsAndLubricants.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(getConsumables.pending, (state) => {
      state.consumablesLoading = true;
    });
    builder.addCase(getConsumables.fulfilled, (state, action) => {
      state.consumablesLoading = false;
      state.consumables = action.payload ? action.payload : [];
    });
    builder.addCase(getConsumables.rejected, (state, action) => {
      state.consumptionLoading = false;
      state.error = action.error;
    });
    builder.addCase(getConsumption.pending, (state) => {
      state.consumablesLoading = true;
    });
    builder.addCase(getConsumption.fulfilled, (state, action) => {
      state.consumptionLoading = false;
      state.consumption = action.payload;
    });
    builder.addCase(getConsumption.rejected, (state, action) => {
      state.consumptionLoading = false;
      state.error = action.error;
    });
    builder.addCase(addRefuel.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(addRefuel.fulfilled, (state, action) => {
      state.loading = false;
      state.refuels.unshift(action.payload.refuel);
    });

    builder.addCase(addRefuel.rejected, (state, action) => {
      state.error = action.error;
    });
    builder.addCase(updateRefuel.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateRefuel.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.refuels = state.refuels.map(oldRefuel => {
          if (oldRefuel.refuelId === action.payload.refuelId) {
            return action.payload;
          } else {
            return oldRefuel;
          }
        });
      }
    });
    builder.addCase(updateRefuel.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(deleteRefuel.fulfilled, (state, action) => {
      if (action.payload) {
        state.refuels = state.refuels.filter(refuel => refuel.refuelId !== action.payload.request.refuelId);
      }
    });
  },
});

export const {
  setError,
  setFuelLuricantsPopover,
  setAverageConsumptionPopover,
  setFuelMap,
  clearFuelMap,
  updateRefuelTemporarily,
  requestDeletingRefuel,
} = fuelsAndLubricantsSlice.actions;

export const selectState = (state: RootState) =>
  state.resources.fuelsAndLubricants;
export default fuelsAndLubricantsSlice.reducer;
